import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Menu, MenuItem, Stack, Typography, Select } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { actionMechanismGroupLabel, type ActionItem, type CustomActionItem } from '../../../redux/EditorSlice';
import { FrameworkAction } from '../../../framework/framework';
import { FrameworkClassType } from '../../../framework/framework-types';
import { useTranslation } from 'react-i18next';

interface CurrentActionEntry {
  parentName: string;
  mechanismName?: string;
  action: FrameworkAction;
}

interface ActionSelectorProps {
  value: string;
  onChange: (newValue: string) => void;
  allowNone?: boolean;
  noneLabel?: string;
  actions?: ActionItem[];
  customActions?: CustomActionItem[];
  ariaLabel?: string;
}

const CLOSE_MENU_DELAY_MS = 200;

/** Collapse consecutive duplicate segments so e.g. arm.arm.move_to_min_position -> arm.move_to_min_position */
function buildActionLabel(parts: string[]): string {
  const deduped = parts.filter((s, i) => i === 0 || s !== parts[i - 1]);
  return deduped.join('.');
}

const ActionSelector: React.FC<ActionSelectorProps> = ({
  value,
  onChange,
  allowNone = false,
  noneLabel = '<none>',
  actions,
  customActions,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const globalActionList = useAppSelector((state) => state.editor.actionList);
  const customActionList = useAppSelector((state) => state.editor.customActionList);
  const actionItems = actions ?? globalActionList;
  const customActionItems = customActions ?? customActionList;

  // Set of all valid option values so MUI Select never receives an out-of-range value (e.g. when actionList is empty or value is from code)
  const validOptionValues = useMemo(() => {
    const set = new Set<string>(['']);
    (actionItems || []).forEach((item: ActionItem) => {
      const parts = [item.parentName || ''];
      if (item.mechanismName) parts.push(item.mechanismName);
      parts.push(item.action?.name ?? '');
      set.add(buildActionLabel(parts));
      if (item.action?.standalone && item.action.name) {
        set.add(buildActionLabel(['UtilityActions', item.action.name]));
      }
    });
    (customActionItems || []).forEach((c: CustomActionItem) => set.add(`Custom.${c.name}`));
    return set;
  }, [actionItems, customActionItems]);

  // Build hierarchical entries with labels and instance names
  const hierarchicalEntries = useMemo(() => {
    const list = (actionItems || []) as ActionItem[];
    type MechanismBucket = { mechanismLabel: string; entries: CurrentActionEntry[] };
    type ParentBucket = { parentLabel: string; mechanisms: MechanismBucket[] };
    const parentMap: Map<string, ParentBucket> = new Map();

    list.forEach((item) => {
      const parentName = item.parentName || '';
      const mechanismName = item.mechanismName || undefined;
      // Do not localize option labels; keep values consistent with globalActionList
      const parentLabel =
        item.mechanismClass?.type === FrameworkClassType.Utility || item.parentClass?.type === FrameworkClassType.Utility
          ? t('actions.utility.groupLabel', 'utility')
          : `${item.parentClass?.name || 'Parent'}${parentName ? `(${parentName})` : ''}`;
      const mechanismLabel = actionMechanismGroupLabel(item);

      if (!parentMap.has(parentLabel)) {
        parentMap.set(parentLabel, { parentLabel, mechanisms: [] });
      }
      const parentBucket = parentMap.get(parentLabel)!;

      let mechBucket = parentBucket.mechanisms.find((m) => m.mechanismLabel === mechanismLabel);
      if (!mechBucket) {
        mechBucket = { mechanismLabel, entries: [] };
        parentBucket.mechanisms.push(mechBucket);
      }
      mechBucket.entries.push({ parentName, mechanismName, action: item.action });
    });
    // Append Custom actions as their own group with variable names
    const customs = (customActionItems || []) as CustomActionItem[];
    if (customs.length > 0) {
      const parentLabel = 'Custom';
      const mechanismLabel = 'Custom';
      const entries: CurrentActionEntry[] = customs.map((c) => ({
        parentName: 'Custom',
        mechanismName: undefined,
        action: { name: c.name } as FrameworkAction,
      }));
      const parentBucket: ParentBucket = {
        parentLabel,
        mechanisms: [{ mechanismLabel, entries }],
      };
      parentMap.set(`__custom__`, parentBucket);
    }

    return Array.from(parentMap.values());
  }, [actionItems, customActionItems, t]);

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(null);
  const [currentActions, setCurrentActions] = useState<CurrentActionEntry[] | null>(null);
  const [activeMechanismKey, setActiveMechanismKey] = useState<string | null>(null);
  const selectRef = useRef<HTMLDivElement | null>(null);
  const mainMenuPaperRef = useRef<HTMLDivElement | null>(null);
  const submenuPaperRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuHasFocusRef = useRef(false);
  const pointerInsideMenuRef = useRef(false);
  const isMenuOpen = Boolean(menuAnchorEl);
  const isSubmenuOpen = isMenuOpen && Boolean(submenuAnchorEl) && Boolean(currentActions);

  const clearCloseTimer = () => {
    if (closeTimerRef.current == null) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const hasMenuFocus = () => {
    return isElementInMenuSurface(document.activeElement);
  };

  const scheduleCloseMenus = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      if (pointerInsideMenuRef.current || hasMenuFocus()) return;
      handleCloseMenus();
    }, CLOSE_MENU_DELAY_MS);
  };

  const handleOpenMenu = () => {
    clearCloseTimer();
    menuHasFocusRef.current = false;
    pointerInsideMenuRef.current = false;
    setMenuAnchorEl(selectRef.current);
    setSubmenuAnchorEl(null);
    setCurrentActions(null);
    setActiveMechanismKey(null);
  };

  const handleSelectClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (isMenuOpen) {
      handleCloseMenus();
      return;
    }
    handleOpenMenu();
  };

  const handleCloseMenus = () => {
    clearCloseTimer();
    menuHasFocusRef.current = false;
    pointerInsideMenuRef.current = false;
    setMenuAnchorEl(null);
    setSubmenuAnchorEl(null);
    setCurrentActions(null);
    setActiveMechanismKey(null);
  };

  const isElementInMenuSurface = (element: EventTarget | null) => {
    if (!(element instanceof Node)) return false;
    return Boolean(mainMenuPaperRef.current?.contains(element) || submenuPaperRef.current?.contains(element));
  };

  const handleMenuFocus = () => {
    menuHasFocusRef.current = true;
    clearCloseTimer();
  };

  const handleMenuMouseEnter = () => {
    pointerInsideMenuRef.current = true;
    clearCloseTimer();
  };

  const handleMenuMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    if (isElementInMenuSurface(event.relatedTarget)) return;
    pointerInsideMenuRef.current = false;
    scheduleCloseMenus();
  };

  const handleMenuBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (isElementInMenuSurface(event.relatedTarget)) return;
    if (!menuHasFocusRef.current) return;
    menuHasFocusRef.current = false;
    scheduleCloseMenus();
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    handleCloseMenus();
  };

  const openMechanismSubmenu = (anchorEl: HTMLElement, entries: CurrentActionEntry[], mechanismKey: string) => {
    clearCloseTimer();
    setSubmenuAnchorEl(anchorEl);
    setCurrentActions(entries);
    setActiveMechanismKey(mechanismKey);
  };

  const handleMechanismOpen = (
    event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
    entries: CurrentActionEntry[],
    mechanismKey: string
  ) => {
    openMechanismSubmenu(event.currentTarget, entries, mechanismKey);
  };

  const handleMechanismClick = (event: React.MouseEvent<HTMLElement>, entries: CurrentActionEntry[], mechanismKey: string) => {
    openMechanismSubmenu(event.currentTarget, entries, mechanismKey);
  };

  useEffect(() => clearCloseTimer, []);

  const handleSelectAction = (entry: CurrentActionEntry) => {
    const parts = [entry.parentName];
    if (entry.mechanismName) parts.push(entry.mechanismName);
    parts.push(entry.action.name);
    const label = buildActionLabel(parts);
    onChange(label);
    handleCloseMenus();
  };

  return (
    <>
      <Select
        ref={selectRef}
        size="small"
        displayEmpty
        open={false}
        value={validOptionValues.has(value) ? value : ''}
        onOpen={(e) => {
          e.preventDefault();
        }}
        onClick={handleSelectClick}
        onChange={() => {}}
        inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        renderValue={() => {
          const s = value || '';
          if (!s) return allowNone ? noneLabel : t('drivebaseDrawer.select-action', 'Select Action');
          return s;
        }}
        sx={{
          fontSize: 12,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 8 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
        }}
      >
        <MenuItem value="" />
        {value && validOptionValues.has(value) && <MenuItem value={value} sx={{ display: 'none' }} />}
      </Select>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenus}
        autoFocus={false}
        disableEnforceFocus
        slotProps={{
          list: {
            onKeyDown: handleMenuKeyDown,
          },
        }}
        PaperProps={{
          ref: mainMenuPaperRef,
          onBlur: handleMenuBlur,
          onFocus: handleMenuFocus,
          onMouseEnter: handleMenuMouseEnter,
          onMouseLeave: handleMenuMouseLeave,
        }}
      >
        {allowNone && (
          <MenuItem
            key="none-option"
            sx={{ pl: 2, fontSize: 12 }}
            onClick={() => {
              onChange('');
              handleCloseMenus();
            }}
          >
            {noneLabel}
          </MenuItem>
        )}
        {hierarchicalEntries.map((entry) => (
          <React.Fragment key={`parent-${entry.parentLabel}`}>
            {entry.mechanisms.map((mech) => {
              const mechanismKey = `mech-${entry.parentLabel}-${mech.mechanismLabel}`;
              const isActiveMechanism = activeMechanismKey === mechanismKey;
              return (
                <MenuItem
                  key={mechanismKey}
                  selected={isActiveMechanism}
                  sx={{
                    pl: 2,
                    fontSize: 12,
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'action.selected' },
                  }}
                  onClick={(e) => handleMechanismClick(e, mech.entries, mechanismKey)}
                  onFocus={(e) => handleMechanismOpen(e, mech.entries, mechanismKey)}
                  onMouseEnter={(e) => handleMechanismOpen(e, mech.entries, mechanismKey)}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography variant="caption">{mech.mechanismLabel}</Typography>
                    <ChevronRight fontSize="small" />
                  </Stack>
                </MenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </Menu>
      <Menu
        anchorEl={submenuAnchorEl}
        open={isSubmenuOpen}
        onClose={handleCloseMenus}
        autoFocus={false}
        disableEnforceFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          // The submenu's full-screen Modal root sits above the parent menu; let hover pass through to parent rows.
          root: {
            sx: { pointerEvents: 'none' },
          },
          list: {
            onKeyDown: handleMenuKeyDown,
          },
        }}
        PaperProps={{
          ref: submenuPaperRef,
          onBlur: handleMenuBlur,
          onFocus: handleMenuFocus,
          onMouseEnter: handleMenuMouseEnter,
          onMouseLeave: handleMenuMouseLeave,
          sx: { pointerEvents: 'auto' },
        }}
      >
        {(currentActions || []).map((entryItem) => (
          <MenuItem
            key={`action-${entryItem.parentName}-${entryItem.mechanismName ?? 'root'}-${entryItem.action.name}`}
            onClick={() => handleSelectAction(entryItem)}
            sx={{ pl: 2, fontSize: 12 }}
          >
            {entryItem.action.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ActionSelector;
